import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Collapse,
  CircularProgress,
  Paper,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Send as SendIcon,
  ContentPaste as InsertIcon,
  Clear as ClearIcon,
  AutoAwesome as SparkleIcon,
  Person as UserIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import AtomIcon from '../common/AtomIcon';
import FlexService from '../../services/FlexService';

/**
 * FlexAiAssistant - AI-powered script generation panel
 *
 * Features:
 * - Natural language input
 * - Generate FLEX scripts
 * - Insert generated code into editor
 * - Chat-like interface with message history (embedded mode)
 */
function FlexAiAssistant({
  onInsert,
  existingScript = '',
  isCollapsed = false,
  onToggleCollapse,
  embedded = false, // When true, renders as chat-like interface in right panel
}) {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState(null);
  // Chat history for embedded mode
  const [messages, setMessages] = useState([]);
  // For non-embedded mode, keep single result
  const [result, setResult] = useState(null);

  const messagesEndRef = useRef(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    if (embedded && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, embedded]);

  // Generate script from prompt
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) return;

    const userPrompt = prompt.trim();
    setIsGenerating(true);
    setError(null);

    // In embedded mode, add user message to chat
    if (embedded) {
      setMessages((prev) => [
        ...prev,
        { type: 'user', content: userPrompt, timestamp: Date.now() },
      ]);
      setPrompt('');
    }

    try {
      const response = await FlexService.generateScript({
        prompt: userPrompt,
        existingScript: existingScript || undefined,
        includeComments: true,
      });

      if (response.success) {
        if (embedded) {
          // Add AI response to chat
          setMessages((prev) => [
            ...prev,
            {
              type: 'assistant',
              content: response.script,
              metadata: {
                processingTimeMs: response.processingTimeMs,
                usedEntities: response.usedEntities,
              },
              timestamp: Date.now(),
            },
          ]);
        } else {
          setResult(response);
        }
      } else {
        const errorMsg = response.error || 'Generation failed';
        setError(errorMsg);
        if (embedded) {
          setMessages((prev) => [
            ...prev,
            { type: 'error', content: errorMsg, timestamp: Date.now() },
          ]);
        }
      }
    } catch (err) {
      const errorMsg = err.message || 'Failed to generate script';
      setError(errorMsg);
      if (embedded) {
        setMessages((prev) => [
          ...prev,
          { type: 'error', content: errorMsg, timestamp: Date.now() },
        ]);
      }
    } finally {
      setIsGenerating(false);
      if (!embedded) {
        setPrompt('');
      }
    }
  }, [prompt, existingScript, embedded]);

  // Insert generated script into editor
  const handleInsert = useCallback((script) => {
    if (script && onInsert) {
      onInsert(script);
    }
  }, [onInsert]);

  // Insert from non-embedded result
  const handleInsertResult = useCallback(() => {
    if (result?.script && onInsert) {
      onInsert(result.script);
      setResult(null);
      setPrompt('');
    }
  }, [result, onInsert]);

  // Clear results/messages
  const handleClear = useCallback(() => {
    setResult(null);
    setError(null);
    setPrompt('');
    setMessages([]);
  }, []);

  // Handle Enter key
  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleGenerate();
      }
    },
    [handleGenerate]
  );

  // Quick prompts
  const quickPrompts = [
    'Count all areas',
    'List active locations',
    'Validate inventory levels',
    'Log warehouse status',
  ];

  // Render a single message in embedded chat mode
  const renderMessage = (message, index) => {
    const isUser = message.type === 'user';
    const isError = message.type === 'error';

    return (
      <Box
        key={index}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isUser ? 'flex-end' : 'flex-start',
          mb: 1.5,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            maxWidth: '90%',
            flexDirection: isUser ? 'row-reverse' : 'row',
          }}
        >
          {/* Avatar */}
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: isUser
                ? 'primary.main'
                : isError
                ? 'error.main'
                : 'secondary.main',
              flexShrink: 0,
            }}
          >
            {isUser ? (
              <UserIcon sx={{ fontSize: 14, color: 'white' }} />
            ) : (
              <AtomIcon sx={{ fontSize: 14 }} />
            )}
          </Box>

          {/* Message content */}
          <Paper
            sx={{
              p: 1.5,
              bgcolor: isUser
                ? 'primary.main'
                : isError
                ? 'error.light'
                : 'background.paper',
              color: isUser || isError ? 'white' : 'text.primary',
              borderRadius: 2,
              borderTopRightRadius: isUser ? 0 : 2,
              borderTopLeftRadius: isUser ? 2 : 0,
            }}
          >
            {isUser ? (
              <Typography variant="body2" sx={{ fontSize: 12 }}>
                {message.content}
              </Typography>
            ) : isError ? (
              <Typography variant="body2" sx={{ fontSize: 12 }}>
                {message.content}
              </Typography>
            ) : (
              <Box>
                {/* Metadata chips */}
                {message.metadata && (
                  <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
                    {message.metadata.processingTimeMs && (
                      <Chip
                        label={`${message.metadata.processingTimeMs}ms`}
                        size="small"
                        sx={{ height: 16, fontSize: 9 }}
                      />
                    )}
                    {message.metadata.usedEntities?.map((entity) => (
                      <Chip
                        key={entity}
                        label={entity}
                        size="small"
                        color="info"
                        sx={{ height: 16, fontSize: 9 }}
                      />
                    ))}
                  </Box>
                )}
                {/* Script content */}
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    fontSize: 11,
                    fontFamily: "'IBM Plex Mono', monospace",
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {message.content}
                </Box>
                {/* Insert button */}
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<InsertIcon sx={{ fontSize: 14 }} />}
                  onClick={() => handleInsert(message.content)}
                  sx={{
                    mt: 1,
                    fontSize: 10,
                    py: 0.25,
                  }}
                >
                  Insert
                </Button>
              </Box>
            )}
          </Paper>
        </Box>
      </Box>
    );
  };

  // Embedded mode - chat-like interface
  if (embedded) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 0.75,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.default',
            flexShrink: 0,
          }}
        >
          <AtomIcon sx={{ fontSize: 16 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: 12, flex: 1 }}>
            AI Assistant
          </Typography>
          {isGenerating && (
            <CircularProgress size={14} sx={{ color: 'primary.main' }} />
          )}
          {messages.length > 0 && (
            <Tooltip title="Clear chat">
              <IconButton size="small" onClick={handleClear} sx={{ p: 0.25 }}>
                <DeleteIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Messages area */}
        <Box
          sx={{
            flex: 1,
            overflow: 'auto',
            p: 1.5,
            minHeight: 0,
            bgcolor: 'background.default',
          }}
        >
          {messages.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <AtomIcon sx={{ fontSize: 32, mb: 1, opacity: 0.5 }} />
              <Typography variant="body2" sx={{ fontSize: 11, textAlign: 'center' }}>
                Describe what the script should do
              </Typography>
              {/* Quick prompts */}
              <Box sx={{ mt: 1.5, display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center' }}>
                {quickPrompts.slice(0, 2).map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    size="small"
                    variant="outlined"
                    onClick={() => setPrompt(suggestion)}
                    sx={{ fontSize: 10, height: 22 }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <>
              {messages.map((message, index) => renderMessage(message, index))}
              <div ref={messagesEndRef} />
            </>
          )}
        </Box>

        {/* Input area */}
        <Box
          sx={{
            p: 1,
            borderTop: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-end' }}>
            <TextField
              size="small"
              placeholder="Describe what to generate..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              fullWidth
              multiline
              maxRows={3}
              disabled={isGenerating}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: 12,
                },
              }}
            />
            <IconButton
              color="primary"
              onClick={handleGenerate}
              disabled={!prompt.trim() || isGenerating}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&:disabled': { bgcolor: 'action.disabled' },
              }}
            >
              {isGenerating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <SendIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  }

  // Non-embedded mode - original collapsible panel
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 0.5,
          borderBottom: isCollapsed ? 0 : 1,
          borderColor: 'divider',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={onToggleCollapse}
      >
        <IconButton size="small" sx={{ p: 0.5 }}>
          {isCollapsed ? (
            <ExpandIcon fontSize="small" />
          ) : (
            <CollapseIcon fontSize="small" />
          )}
        </IconButton>

        <AtomIcon sx={{ fontSize: 18 }} />
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
          AI Assistant
        </Typography>

        {isGenerating && (
          <CircularProgress size={16} sx={{ color: 'primary.main' }} />
        )}
      </Box>

      {/* Content */}
      <Collapse in={!isCollapsed}>
        <Box sx={{ p: 2 }}>
          {/* Prompt input */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              size="small"
              placeholder="Describe what the script should do..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              onKeyPress={handleKeyPress}
              fullWidth
              multiline
              maxRows={3}
              disabled={isGenerating}
              InputProps={{
                startAdornment: (
                  <SparkleIcon
                    sx={{ color: 'text.secondary', fontSize: 18, mr: 1 }}
                  />
                ),
              }}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleGenerate}
              disabled={!prompt.trim() || isGenerating}
              sx={{ minWidth: 80 }}
            >
              {isGenerating ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Generate'
              )}
            </Button>
          </Box>

          {/* Quick prompts */}
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 2 }}>
            {quickPrompts.map((suggestion) => (
              <Chip
                key={suggestion}
                label={suggestion}
                size="small"
                variant="outlined"
                onClick={() => setPrompt(suggestion)}
                sx={{ fontSize: 11, height: 24 }}
              />
            ))}
          </Box>

          {/* Error display */}
          {error && (
            <Paper
              sx={{
                p: 1.5,
                mb: 2,
                bgcolor: 'error.main',
                color: 'error.contrastText',
              }}
            >
              <Typography variant="body2">{error}</Typography>
            </Paper>
          )}

          {/* Result display */}
          {result && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Generated Script
                </Typography>
                {result.processingTimeMs && (
                  <Chip
                    label={`${result.processingTimeMs}ms`}
                    size="small"
                    sx={{ height: 18, fontSize: 10 }}
                  />
                )}
                {result.usedEntities?.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {result.usedEntities.map((entity) => (
                      <Chip
                        key={entity}
                        label={entity}
                        size="small"
                        color="info"
                        sx={{ height: 18, fontSize: 10 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Paper
                sx={{
                  p: 1.5,
                  mb: 1,
                  bgcolor: 'grey.900',
                  border: 1,
                  borderColor: 'divider',
                  maxHeight: 200,
                  overflow: 'auto',
                }}
              >
                <Box
                  component="pre"
                  sx={{
                    m: 0,
                    fontSize: 12,
                    fontFamily: "'IBM Plex Mono', monospace",
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    color: '#e0e0e0',
                  }}
                >
                  {result.script}
                </Box>
              </Paper>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<InsertIcon />}
                  onClick={handleInsertResult}
                >
                  Insert into Editor
                </Button>
                <Tooltip title="Clear">
                  <IconButton size="small" onClick={handleClear}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

export default FlexAiAssistant;
